/**
 * Equipment Hooks
 *
 * TanStack Query hooks for equipment management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, buildQueryString, uploadFile } from '../client';
import { queryKeys } from '../query-keys';
import type {
  Equipment,
  EquipmentListResponse,
  EquipmentFilters,
  CreateEquipmentRequest,
  UpdateEquipmentRequest,
  ImportResult,
} from '../types/equipment';

/**
 * Fetch paginated equipment list
 */
export function useEquipment(filters: EquipmentFilters = {}) {
  return useQuery({
    queryKey: queryKeys.equipment.list(filters),
    queryFn: () =>
      apiFetch<EquipmentListResponse>(`/api/equipment${buildQueryString(filters)}`),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Fetch single equipment by ID
 */
export function useEquipmentDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.equipment.detail(id),
    queryFn: () => apiFetch<Equipment>(`/api/equipment/${id}`),
    enabled: !!id,
  });
}

/**
 * Fetch equipment by tag ID (for kiosk scanning)
 */
export function useEquipmentByTag(tagId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.equipment.byTag(tagId),
    queryFn: async () => {
      const response = await apiFetch<Equipment>(
        `/api/equipment${buildQueryString({ search: tagId, limit: 1 })}`
      );
      const items = (response as unknown as EquipmentListResponse).items;
      if (!items.length) {
        throw new Error('Equipment not found. Please check the ID and try again.');
      }
      return items[0]!;
    },
    enabled: options?.enabled !== false && !!tagId && tagId.length >= 3,
    staleTime: 10_000, // 10 seconds
    retry: false,
  });
}

/**
 * Create new equipment
 */
export function useCreateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEquipmentRequest) =>
      apiFetch<Equipment>('/api/equipment', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
  });
}

/**
 * Update existing equipment
 */
export function useUpdateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateEquipmentRequest & { id: string }) =>
      apiFetch<Equipment>(`/api/equipment/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.detail(variables.id) });
    },
  });
}

/**
 * Delete equipment
 */
export function useDeleteEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/equipment/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
  });
}

/**
 * Import equipment from CSV
 */
export function useImportEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadFile<ImportResult>('/api/equipment/import', file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
  });
}
